/**
 * ConceptMarkdown — brand-styled Markdown renderer for Tour Concept bodies.
 * Supports GFM: bold, italics, headings, tables, lists, blockquotes, ---.
 * Typography mirrors the pollendots.com brief language (Fraunces serif
 * headings, mono table headers, petal dividers).
 */
import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const INK = '#030922';
const MAROON = '#581C2B';
const FOREST = '#163224';
const PETAL = '#F3BAA8';
const GRAY = '#787878';
const SERIF = "'Fraunces', Georgia, serif";
const MONO = "'JetBrains Mono', monospace";

const mk = (compact) => ({
  h1: ({ children }) => (
    <h2 className={compact ? 'text-lg mt-5 mb-2' : 'text-2xl sm:text-3xl mt-10 mb-4'}
        style={{ fontFamily: SERIF, fontWeight: 500, color: INK, lineHeight: 1.25 }}>{children}</h2>
  ),
  h2: ({ children }) => (
    <h3 className={compact ? 'text-base mt-4 mb-1.5' : 'text-xl sm:text-2xl mt-8 mb-3'}
        style={{ fontFamily: SERIF, fontWeight: 500, color: MAROON, lineHeight: 1.3 }}>{children}</h3>
  ),
  h3: ({ children }) => (
    <h4 className={compact ? 'text-sm mt-3 mb-1' : 'text-lg mt-6 mb-2'}
        style={{ fontFamily: SERIF, fontWeight: 500, fontStyle: 'italic', color: FOREST }}>{children}</h4>
  ),
  h4: ({ children }) => (
    <h5 className="text-[11px] uppercase mt-5 mb-2"
        style={{ fontFamily: MONO, letterSpacing: '0.2em', color: MAROON }}>{children}</h5>
  ),
  p: ({ children }) => (
    <p className={compact ? 'text-[13px] leading-[1.65] my-2' : 'text-[15px] sm:text-base leading-[1.75] my-3'}
       style={{ color: GRAY }}>{children}</p>
  ),
  strong: ({ children }) => <strong style={{ color: INK, fontWeight: 600 }}>{children}</strong>,
  em: ({ children }) => <em style={{ color: FOREST }}>{children}</em>,
  a: ({ href, children }) => (
    <a href={href} target="_blank" rel="noreferrer"
       style={{ color: MAROON, textDecoration: 'underline', textUnderlineOffset: 3 }}>{children}</a>
  ),
  ul: ({ children }) => <ul className="my-3 pl-5 space-y-1.5 list-disc marker:text-[#F3BAA8]" style={{ color: GRAY }}>{children}</ul>,
  ol: ({ children }) => <ol className="my-3 pl-5 space-y-1.5 list-decimal" style={{ color: GRAY }}>{children}</ol>,
  li: ({ children }) => <li className={compact ? 'text-[13px] leading-[1.6]' : 'text-[15px] leading-[1.7]'}>{children}</li>,
  blockquote: ({ children }) => (
    <blockquote className="my-4 pl-4 py-1" style={{ borderLeft: `3px solid ${PETAL}`, color: FOREST, fontFamily: SERIF, fontStyle: 'italic' }}>
      {children}
    </blockquote>
  ),
  hr: () => (
    <div className="flex items-center justify-center gap-3 my-8" aria-hidden="true">
      <span className="h-px flex-1" style={{ background: '#EBDFD6' }} />
      <svg width="16" height="16" viewBox="0 0 25 26" fill={PETAL}>
        <path d="M14.4758 2.53867C14.4758 3.58046 13.8363 8.43343 12.5387 13.1492C11.2411 8.43343 10.6016 3.58046 10.6016 2.53867C10.6016 1.49688 11.4688 0.652344 12.5387 0.652344C13.6085 0.652344 14.4758 1.49688 14.4758 2.53867Z" />
        <path d="M14.4758 23.7836C14.4758 22.7418 13.8363 17.8888 12.5387 13.173C11.2411 17.8888 10.6016 22.7418 10.6016 23.7836C10.6016 24.8254 11.4688 25.6699 12.5387 25.6699C13.6085 25.6699 14.4758 24.8254 14.4758 23.7836Z" />
        <path d="M21.1343 6.23903C20.2159 6.75992 16.2575 9.73047 12.749 13.1924C17.5551 11.9384 22.1531 10.056 23.0715 9.5351C23.9899 9.01421 24.3007 7.85409 23.7658 6.9439C23.2309 6.03372 22.0527 5.71814 21.1343 6.23903Z" />
        <path d="M21.1343 20.0856C20.2159 19.5647 16.2575 16.5941 12.749 13.1322C17.5551 14.3862 22.1531 16.2686 23.0715 16.7895C23.9899 17.3104 24.3007 18.4705 23.7658 19.3807C23.2309 20.2909 22.0527 20.6064 21.1343 20.0856Z" />
        <g transform="scale(-1,1) translate(-25.1,0)">
          <path d="M21.1343 6.23903C20.2159 6.75992 16.2575 9.73047 12.749 13.1924C17.5551 11.9384 22.1531 10.056 23.0715 9.5351C23.9899 9.01421 24.3007 7.85409 23.7658 6.9439C23.2309 6.03372 22.0527 5.71814 21.1343 6.23903Z" />
          <path d="M21.1343 20.0856C20.2159 19.5647 16.2575 16.5941 12.749 13.1322C17.5551 14.3862 22.1531 16.2686 23.0715 16.7895C23.9899 17.3104 24.3007 18.4705 23.7658 19.3807C23.2309 20.2909 22.0527 20.6064 21.1343 20.0856Z" />
        </g>
      </svg>
      <span className="h-px flex-1" style={{ background: '#EBDFD6' }} />
    </div>
  ),
  table: ({ children }) => (
    <div className="my-5 overflow-x-auto rounded-xl border" style={{ borderColor: '#EBDFD6', background: '#fff' }}>
      <table className="w-full text-left" style={{ borderCollapse: 'collapse', minWidth: 480 }}>{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead style={{ background: '#FAF3EF' }}>{children}</thead>,
  th: ({ children }) => (
    <th className="px-3.5 py-2.5 text-[10px] uppercase align-bottom"
        style={{ fontFamily: MONO, letterSpacing: '0.14em', color: MAROON, borderBottom: `1px solid ${PETAL}` }}>
      {children}
    </th>
  ),
  td: ({ children }) => (
    <td className={`px-3.5 align-top ${compact ? 'py-2 text-[12px]' : 'py-2.5 text-[13px] sm:text-sm'}`}
        style={{ color: GRAY, borderBottom: '1px solid #F2EAE4', lineHeight: 1.6 }}>
      {children}
    </td>
  ),
  code: ({ inline, children }) => (
    <code className="px-1.5 py-0.5 rounded text-[12px]" style={{ fontFamily: MONO, background: '#F2EAE4', color: MAROON }}>
      {children}
    </code>
  ),
});

export const ConceptMarkdown = ({ children, compact = false }) => (
  <div data-testid="concept-markdown">
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={mk(compact)}>
      {/* People paste `text\n---` expecting a divider; without the blank
          line Markdown turns the text into a setext heading. Normalize. */}
      {(children || '').replace(/([^\n])\n(-{3,})(\n|$)/g, '$1\n\n$2$3')}
    </ReactMarkdown>
  </div>
);

export default ConceptMarkdown;
